import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const QuemSomosPage = () => {
  useEffect(() => {
    // Update document title
    document.title = 'Quem Somos - Carbone Soluções Financeiras Campinas';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Conheça a Carbone Soluções Financeiras, especialistas em crédito consignado em Campinas. Nossa missão é oferecer atendimento humano, rápido e transparente para sua tranquilidade financeira. Saiba mais sobre nossa história, valores e compromisso com você.');
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://example.com/quem-somos');
    }

    // Add JSON-LD structured data
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "Quem Somos - Carbone Soluções Financeiras",
      "description": "Conheça a história, missão e valores da Carbone Soluções Financeiras. Especialistas em crédito consignado em Campinas com atendimento humano e transparente.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/quem-somos`,
      "mainEntity": {
        "@type": "Organization",
        "name": "Carbone Soluções Financeiras",
        "description": "Empresa especializada em crédito consignado e soluções financeiras"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/b773b139c39d63f2ed29a81ceedc2e90.jpg)'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-navy-950/95 via-navy-900/90 to-navy-950/95"></div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
            Quem Somos
          </h1>
          <p className="text-2xl text-gold-400 font-semibold mb-4">
            Carbone Soluções Financeiras
          </p>
          <p className="text-xl text-white/90 leading-relaxed">
            Crédito com segurança, confiança e transparência
          </p>
        </div>
      </section>

      {/* Institucional Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-navy-50 to-gold-50 rounded-3xl p-12 border-2 border-gold-200 shadow-xl">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-navy-800 leading-relaxed mb-6">
                A <strong className="text-navy-900">Carbone Soluções Financeiras</strong> é um escritório especializado em soluções de crédito, atuando como correspondente bancário com foco em atendimento responsável, transparente e seguro.
              </p>

              <p className="text-lg text-navy-800 leading-relaxed mb-6">
                Trabalhamos com <strong className="text-gold-700">crédito consignado, crédito pessoal e financiamentos</strong>, atendendo aposentados, pensionistas do INSS, trabalhadores CLT, produtores rurais e clientes que buscam crédito veicular, sempre respeitando as normas do sistema financeiro e as políticas das instituições parceiras.
              </p>

              <p className="text-lg text-navy-800 leading-relaxed mb-6">
                Nossa atuação é baseada na <strong className="text-navy-900">ética, na clareza das informações e no compromisso com o cliente</strong>, oferecendo orientação completa desde a análise até a liberação do crédito. Cada operação é conduzida com atenção individual, buscando a melhor alternativa de acordo com o perfil e a necessidade de cada pessoa.
              </p>

              <p className="text-lg text-navy-800 leading-relaxed">
                Mais do que intermediar crédito, nosso objetivo é <strong className="text-gold-700">construir relações de confiança</strong>, proporcionando tranquilidade, segurança e soluções financeiras responsáveis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Valores Section */}
      <section className="py-20 bg-gradient-to-br from-navy-900 to-navy-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Nossos Valores
            </h2>
            <p className="text-xl text-white/80">
              Princípios que guiam nosso trabalho
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-gold-400/30 hover:border-gold-400 transition-all duration-300">
              <div className="w-16 h-16 bg-gold-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-navy-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Transparência</h3>
              <p className="text-white/80 leading-relaxed">
                Informações claras e completas em todas as etapas do processo, sem surpresas ou taxas ocultas.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-gold-400/30 hover:border-gold-400 transition-all duration-300">
              <div className="w-16 h-16 bg-gold-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-heart-line text-3xl text-navy-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Responsabilidade</h3>
              <p className="text-white/80 leading-relaxed">
                Atendimento ético e responsável, sempre respeitando as normas do sistema financeiro.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-gold-400/30 hover:border-gold-400 transition-all duration-300">
              <div className="w-16 h-16 bg-gold-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-user-heart-line text-3xl text-navy-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Compromisso</h3>
              <p className="text-white/80 leading-relaxed">
                Atenção individual e personalizada para encontrar a melhor solução para cada cliente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Diferenciais Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-navy-900 mb-4">
              Por que escolher a Carbone?
            </h2>
            <p className="text-xl text-navy-600">
              Diferenciais que fazem a diferença
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-navy-50 to-white rounded-2xl p-6 border-2 border-navy-100 hover:border-gold-400 transition-all duration-300 hover:shadow-xl">
              <div className="w-12 h-12 bg-gold-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">Agilidade</h3>
              <p className="text-navy-600 text-sm">
                Aprovação rápida e liberação em até 48 horas
              </p>
            </div>

            <div className="bg-gradient-to-br from-navy-50 to-white rounded-2xl p-6 border-2 border-navy-100 hover:border-gold-400 transition-all duration-300 hover:shadow-xl">
              <div className="w-12 h-12 bg-gold-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-lock-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">Segurança</h3>
              <p className="text-navy-600 text-sm">
                100% seguro e em conformidade com a LGPD
              </p>
            </div>

            <div className="bg-gradient-to-br from-navy-50 to-white rounded-2xl p-6 border-2 border-navy-100 hover:border-gold-400 transition-all duration-300 hover:shadow-xl">
              <div className="w-12 h-12 bg-gold-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">Sem Taxas Antecipadas</h3>
              <p className="text-navy-600 text-sm">
                Não cobramos nenhuma taxa antes da aprovação
              </p>
            </div>

            <div className="bg-gradient-to-br from-navy-50 to-white rounded-2xl p-6 border-2 border-navy-100 hover:border-gold-400 transition-all duration-300 hover:shadow-xl">
              <div className="w-12 h-12 bg-gold-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">Atendimento Humanizado</h3>
              <p className="text-navy-600 text-sm">
                Equipe especializada pronta para ajudar você
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-navy-900 to-navy-950">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Pronto para começar?
          </h2>
          <p className="text-xl text-white/80 mb-10">
            Entre em contato conosco e descubra as melhores soluções de crédito para você
          </p>

          <a
            href="https://wa.me/5519971344445?text=Olá! Gostaria de conhecer melhor a Carbone Soluções Financeiras."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-gold-500 hover:bg-gold-600 text-navy-900 font-bold text-xl px-12 py-5 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-1 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-3xl"></i>
            Falar com um especialista
            <i className="ri-arrow-right-line text-2xl"></i>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QuemSomosPage;
